import {
  type BillingInterval,
  captureAdsAttribution as stampAdsAttributionCookies,
  getPlanDisplayName,
  getPlanPriceUsd,
  type PlanTier,
} from "@reactive-resume/utils";

/**
 * Tiny dataLayer helper for the GTM container loaded in `apps/client/index.html`
 * (`GTM-WK6H3LF4`). Custom events that flow through here:
 *
 *   `register_subscribe` — the existing registration-to-plan conversion.
 *     Fired the first time a user transitions from "no plan" to an active
 *     trial or paid plan, once per user lifetime.
 *
 *   `click_and_register` — the cross-domain Google Ads conversion. Fires
 *     when a user clicked a register CTA on `resumebuilder.job-sea.com` and
 *     then completed registration on `careerbldr.com`. The jobsea side
 *     stamps the link with `utm_source=resumebuilder-jobsea`; we capture
 *     that on landing (`captureJobseaReferralIfPresent`), then
 *     `trackClickAndRegister` consumes the breadcrumb on successful
 *     registration. Idempotent per user.
 *
 *   `submit_register_form` — a real account was created. Fired from JS
 *     only after `/auth/register` succeeds (or a new OAuth user lands
 *     on `/auth/callback?signup=1`). Do not also push `sign_up`; that
 *     key event would double-count the same signup.
 *
 * Design goals:
 *   - Never throw: a tracking failure must never break the app.
 *   - GA4 ecommerce ready: `register_subscribe` carries the enhanced
 *     ecommerce object so the GA4 → Google Ads link can report conversion
 *     value, currency, and item details automatically.
 *   - Idempotent: per-user `localStorage` gates prevent re-firing on
 *     refreshes, upgrades, or future Stripe successes.
 */

const SESSION_REGISTER_FLAG = "careerbldr:registered-in-session";
const SESSION_REGISTER_AT = "careerbldr:registered-at";
const REGISTER_SUBSCRIBE_FIRED_PREFIX = "careerbldr:register-subscribe-fired:";
const ONBOARDING_STARTED_AT = "careerbldr:onboarding-started-at";
const FIRST_REAL_EXPERIENCE_USER_PREFIX = "careerbldr:first-real-experience-user:";

const JOBSEA_FLAG_KEY = "careerbldr:from-jobsea-at";
// 7-day attribution window: long enough that a user can browse → leave →
// come back later and still register, short enough that stale flags from
// months-old visits don't pollute the conversion.
const JOBSEA_FLAG_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const CLICK_AND_REGISTER_FIRED_PREFIX = "careerbldr:click-and-register-fired:";
const SUBMIT_REGISTER_FORM_FIRED_PREFIX = "careerbldr:submit-register-form-fired:";

export type RegisterMethod = "email" | "github" | "google";

const NEW_ACCOUNT_MAX_AGE_MS = 10 * 60 * 1000;

export const isNewAccount = (createdAt: Date | string | null | undefined): boolean => {
  if (createdAt == null) return false;
  try {
    const t = createdAt instanceof Date ? createdAt.getTime() : Date.parse(String(createdAt));
    if (!Number.isFinite(t)) return false;
    const age = Date.now() - t;
    return age >= 0 && age <= NEW_ACCOUNT_MAX_AGE_MS;
  } catch {
    return false;
  }
};

type AnyRecord = Record<string, unknown>;

type TikTokQueue = {
  identify: (user: { email?: string; phone_number?: string; external_id?: string }) => void;
  track: (
    event: string,
    properties?: Record<string, unknown>,
    options?: { event_id?: string },
  ) => void;
};

declare global {
  interface Window {
    dataLayer?: AnyRecord[];
    oaiq?: (...args: unknown[]) => void;
    ttq?: TikTokQueue;
  }
}

const safeWindow = (): Window | undefined => (typeof window === "undefined" ? undefined : window);

const sha256 = async (value: string): Promise<string | null> => {
  const crypto = safeWindow()?.crypto;
  if (!crypto?.subtle) return null;

  try {
    const bytes = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join(
      "",
    );
  } catch {
    return null;
  }
};

const trackOpenAiPlanStarted = async (ctx: SubscriptionContext): Promise<void> => {
  const oaiq = safeWindow()?.oaiq;
  if (!oaiq || !ctx.transactionId || !ctx.serverConfirmed) return;

  try {
    const [emailHash, externalIdHash] = await Promise.all([
      ctx.email ? sha256(ctx.email.trim().toLowerCase()) : null,
      ctx.userId ? sha256(ctx.userId.trim()) : null,
    ]);

    if (emailHash || externalIdHash) {
      oaiq("init", {
        user: {
          ...(emailHash ? { email_sha256: emailHash } : {}),
          ...(externalIdHash ? { external_id_sha256: externalIdHash } : {}),
        },
      });
    }

    oaiq(
      "measure",
      ctx.startsTrial ? "trial_started" : "subscription_created",
      {
        type: "plan_enrollment",
        plan_id: `${ctx.tier}_${ctx.interval}`,
        ...(ctx.startsTrial
          ? {}
          : {
              amount: Math.round(getPlanPriceUsd(ctx.tier, ctx.interval) * 100),
              currency: "USD",
            }),
      },
      { event_id: ctx.transactionId },
    );
  } catch {
    // Tracking must never break the app.
  }
};

const generateEventId = (): string => {
  const w = safeWindow();
  const cryptoObj = w?.crypto as { randomUUID?: () => string } | undefined;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  return `evt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

let cachedUserId: string | null = null;
let cachedEmail: string | null = null;
let cachedTikTokIdentity: string | null = null;
let cachedTikTokIdentityPromise: Promise<void> | null = null;

/** Trim + lowercase. Empty / invalid values are dropped so GTM never hashes junk. */
export const normalizeAnalyticsEmail = (email?: string | null): string | null => {
  const value = (email ?? "").trim().toLowerCase();
  if (!value || !value.includes("@") || value.startsWith("@") || value.endsWith("@")) {
    return null;
  }
  return value;
};

const identifyTikTokUser = async (userId: string | null, email?: string | null): Promise<void> => {
  const ttq = safeWindow()?.ttq;
  if (!ttq || !userId) return;

  const normalizedEmail = normalizeAnalyticsEmail(email);
  const identityKey = `${userId}:${normalizedEmail ?? ""}`;
  if (cachedTikTokIdentity === identityKey && cachedTikTokIdentityPromise) {
    return cachedTikTokIdentityPromise;
  }
  cachedTikTokIdentity = identityKey;

  cachedTikTokIdentityPromise = (async () => {
    try {
      const [emailHash, externalIdHash] = await Promise.all([
        normalizedEmail ? sha256(normalizedEmail) : null,
        sha256(userId.trim()),
      ]);

      if (!emailHash && !externalIdHash) return;
      ttq.identify({
        ...(emailHash ? { email: emailHash } : {}),
        ...(externalIdHash ? { external_id: externalIdHash } : {}),
      });
    } catch {
      // Tracking must never break the app.
    }
  })();

  return cachedTikTokIdentityPromise;
};

const buildTikTokPlanProperties = (
  tier: NonNullable<PlanTier>,
  interval: BillingInterval,
  includeValue: boolean,
): Record<string, unknown> => {
  const value = getPlanPriceUsd(tier, interval);
  return {
    contents: [
      {
        content_id: `${tier}_${interval}`,
        content_type: "product",
        content_name: `${getPlanDisplayName(tier) ?? tier} (${interval})`,
      },
    ],
    ...(includeValue ? { value, currency: "USD" } : {}),
  };
};

const trackTikTokCompleteRegistration = async (
  userId: string,
  email?: string | null,
): Promise<void> => {
  const ttq = safeWindow()?.ttq;
  if (!ttq) return;

  try {
    await identifyTikTokUser(userId, email);
    ttq.track("CompleteRegistration", {}, { event_id: `registration_${userId}` });
  } catch {
    // Tracking must never break the app.
  }
};

export const trackTikTokInitiateCheckout = async (ctx: {
  tier: NonNullable<PlanTier>;
  interval: BillingInterval;
  eventId: string;
  userId: string | null;
  email?: string | null;
}): Promise<void> => {
  const ttq = safeWindow()?.ttq;
  if (!ttq || !ctx.eventId) return;

  try {
    await identifyTikTokUser(ctx.userId, ctx.email);
    ttq.track("InitiateCheckout", buildTikTokPlanProperties(ctx.tier, ctx.interval, true), {
      event_id: ctx.eventId,
    });
  } catch {
    // Tracking must never break the app.
  }
};

const trackTikTokPlanStarted = async (ctx: SubscriptionContext): Promise<void> => {
  const ttq = safeWindow()?.ttq;
  if (!ttq || !ctx.transactionId || !ctx.serverConfirmed) return;

  try {
    await identifyTikTokUser(ctx.userId, ctx.email);
    ttq.track(
      ctx.startsTrial ? "StartTrial" : "Subscribe",
      buildTikTokPlanProperties(ctx.tier, ctx.interval, !ctx.startsTrial),
      { event_id: ctx.transactionId },
    );
  } catch {
    // Tracking must never break the app.
  }
};

const withIdentity = (payload: AnyRecord): AnyRecord => {
  if (cachedEmail && payload.user_email === undefined) payload.user_email = cachedEmail;
  return payload;
};

const serializeParams = (params: AnyRecord): AnyRecord => {
  const out: AnyRecord = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "boolean") out[key] = value ? "true" : "false";
    else out[key] = value;
  }
  return out;
};

const push = (payload: AnyRecord): void => {
  const w = safeWindow();
  if (!w) return;
  try {
    w.dataLayer = w.dataLayer ?? [];
    w.dataLayer.push({ event_id: generateEventId(), ...withIdentity(payload) });
  } catch {
    // Tracking must never break the app.
  }
};

/**
 * Stamp GA4 User-ID after login/register. GTM listens for `user_identified`
 * and sets `user_id` on the Google Tag so later hits stitch to the same person.
 * `user_email` is for Enhanced Conversions only (GTM User-Provided Data). It
 * must not be mapped as a GA4 event parameter.
 */
export const identifyAnalyticsUser = (userId: string | null, email?: string | null): void => {
  if (!userId) {
    cachedUserId = null;
    cachedEmail = null;
    return;
  }
  const normalized = normalizeAnalyticsEmail(email) ?? cachedEmail;
  if (cachedUserId === userId && cachedEmail === normalized) return;
  cachedUserId = userId;
  cachedEmail = normalized;
  void identifyTikTokUser(userId, normalized);
  push({
    event: "user_identified",
    user_id: userId,
    app_user_id: userId,
    ...(cachedEmail ? { user_email: cachedEmail } : {}),
  });
};

export const clearAnalyticsUser = (): void => {
  cachedUserId = null;
  cachedEmail = null;
  cachedTikTokIdentity = null;
  cachedTikTokIdentityPromise = null;
  const w = safeWindow();
  try {
    // `null` clears these keys in GTM's data model; `undefined` is dropped and
    // would leave the previous email in place after logout.
    w?.dataLayer?.push({ user_email: null, user_id: null, app_user_id: null });
  } catch {
    // ignore
  }
};

const elapsedSince = (key: string): number | null => {
  const local = safeLocal();
  if (!local) return null;
  try {
    const raw = local.getItem(key);
    if (!raw) return null;
    const ms = Date.now() - Number(raw);
    return Number.isFinite(ms) && ms >= 0 ? ms : null;
  } catch {
    return null;
  }
};

export const markOnboardingStartedAt = (): void => {
  const local = safeLocal();
  if (!local) return;
  try {
    local.setItem(ONBOARDING_STARTED_AT, Date.now().toString());
  } catch {
    // ignore quota / privacy-mode failures
  }
};

export const onboardingElapsedMs = (): number | null => elapsedSince(ONBOARDING_STARTED_AT);

export const withOnboardingTiming = (params: AnyRecord = {}): AnyRecord => {
  const ms = onboardingElapsedMs();
  return ms === null ? params : { ...params, time_to_continue_ms: ms };
};

const safeSession = () => {
  const w = safeWindow();
  try {
    return w?.sessionStorage;
  } catch {
    return undefined;
  }
};

const safeLocal = () => {
  const w = safeWindow();
  try {
    return w?.localStorage;
  } catch {
    return undefined;
  }
};

/**
 * Drop a session breadcrumb the moment a registration API call succeeds.
 * Read later by `trackRegisterSubscribe` so the conversion event can report
 * `from_registration: true` and a `time_to_subscribe_ms` value when the
 * user signs up and pays inside the same session.
 */
export const markRegistrationStarted = (): void => {
  const ss = safeSession();
  if (!ss) return;
  try {
    ss.setItem(SESSION_REGISTER_FLAG, "1");
    ss.setItem(SESSION_REGISTER_AT, Date.now().toString());
  } catch {
    // ignore quota / privacy-mode failures
  }
};

const consumeRegistrationFlag = (): { fromRegistration: boolean; ageMs: number | null } => {
  const ss = safeSession();
  if (!ss) return { fromRegistration: false, ageMs: null };
  try {
    const flag = ss.getItem(SESSION_REGISTER_FLAG);
    const ts = ss.getItem(SESSION_REGISTER_AT);
    if (!flag) return { fromRegistration: false, ageMs: null };

    ss.removeItem(SESSION_REGISTER_FLAG);
    ss.removeItem(SESSION_REGISTER_AT);

    const ageMs = ts ? Date.now() - Number(ts) : null;
    return { fromRegistration: true, ageMs: Number.isFinite(ageMs) ? ageMs : null };
  } catch {
    return { fromRegistration: false, ageMs: null };
  }
};

type SubscriptionContext = {
  tier: NonNullable<PlanTier>;
  interval: BillingInterval;
  transactionId: string | null;
  userId: string | null;
  email?: string | null;
  serverConfirmed?: boolean;
  startsTrial?: boolean;
};

const buildEcommercePayload = ({ tier, interval, transactionId }: SubscriptionContext) => {
  const value = getPlanPriceUsd(tier, interval);
  const itemId = `${tier}_${interval}`;
  const itemName = `${getPlanDisplayName(tier) ?? tier} (${interval})`;
  return {
    transaction_id: transactionId ?? itemId,
    currency: "USD",
    value,
    tier,
    interval,
    items: [
      {
        item_id: itemId,
        item_name: itemName,
        item_category: "subscription",
        item_variant: interval,
        price: value,
        quantity: 1,
      },
    ],
  };
};

const readDocumentCookie = (cookieString: string, name: string): string | undefined => {
  const parts = cookieString.split(";");
  for (const part of parts) {
    const separator = part.indexOf("=");
    if (separator === -1) continue;
    if (part.slice(0, separator).trim() !== name) continue;
    const value = part.slice(separator + 1).trim();
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return undefined;
};

/** Persist Google Ads / UTM query params into first-party cookies for Stripe Checkout. */
export const captureAdsAttribution = (): void => {
  const w = safeWindow();
  if (!w) return;

  const secure = w.location.protocol === "https:";
  stampAdsAttributionCookies({
    search: w.location.search,
    pathname: w.location.pathname,
    cookies: {
      get: (name) => readDocumentCookie(w.document.cookie, name),
      set: (name, value, maxAgeSec) => {
        w.document.cookie = `${name}=${encodeURIComponent(
          value,
        )}; Path=/; Max-Age=${maxAgeSec}; SameSite=Lax${secure ? "; Secure" : ""}`;
      },
    },
  });
};

/**
 * Detect a `resumebuilder.job-sea.com` referral on the current URL and
 * stamp localStorage with the timestamp of the click. Called once on app
 * boot from `main.tsx`; the breadcrumb is consumed later by
 * `trackClickAndRegister` after a successful registration.
 *
 * Match logic:
 *   - `utm_source` exactly `resumebuilder-jobsea` (set by the jobsea CTA
 *     links — primary source of truth).
 *   - Or `from=jobsea` (manual override / future flexibility).
 *   - Case-insensitive on both.
 *
 * Idempotent: rewrites the flag on every jobsea landing, so the most
 * recent click resets the TTL window.
 */
export const captureJobseaReferralIfPresent = (): void => {
  const w = safeWindow();
  if (!w) return;

  let isJobsea = false;
  try {
    const params = new URLSearchParams(w.location.search);
    const utmSource = (params.get("utm_source") ?? "").toLowerCase();
    const from = (params.get("from") ?? "").toLowerCase();
    isJobsea = utmSource === "resumebuilder-jobsea" || from === "jobsea";
  } catch {
    return;
  }
  if (!isJobsea) return;

  const local = safeLocal();
  if (!local) return;
  try {
    local.setItem(JOBSEA_FLAG_KEY, Date.now().toString());
  } catch {
    // ignore quota / privacy-mode failures
  }
};

const consumeJobseaFlag = (): boolean => {
  const local = safeLocal();
  if (!local) return false;
  try {
    const raw = local.getItem(JOBSEA_FLAG_KEY);
    if (!raw) return false;

    const ts = Number(raw);
    if (!Number.isFinite(ts)) {
      local.removeItem(JOBSEA_FLAG_KEY);
      return false;
    }

    const ageMs = Date.now() - ts;
    // Always clear once read — the flag represents a single attribution
    // window and is consumed at first registration.
    local.removeItem(JOBSEA_FLAG_KEY);

    return ageMs >= 0 && ageMs <= JOBSEA_FLAG_TTL_MS;
  } catch {
    return false;
  }
};

/**
 * Push the cross-domain `click_and_register` conversion event when a user
 * who arrived from `resumebuilder.job-sea.com` completes registration on
 * `careerbldr.com`. No-op if the jobsea breadcrumb is missing/expired or
 * the event already fired for this user.
 *
 * @returns true if the event was pushed, false if it was a no-op.
 */
export const trackClickAndRegister = (
  userId: string | null,
  method: RegisterMethod = "email",
): boolean => {
  const local = safeLocal();
  const key = userId ? `${CLICK_AND_REGISTER_FIRED_PREFIX}${userId}` : null;

  if (local && key) {
    try {
      if (local.getItem(key) === "1") return false;
    } catch {
      // fall through and still attempt to fire
    }
  }

  if (!consumeJobseaFlag()) return false;

  push({
    event: "click_and_register",
    source: "resumebuilder-jobsea",
    method,
    ...(userId ? { user_id: userId } : {}),
  });

  if (local && key) {
    try {
      local.setItem(key, "1");
    } catch {
      // ignore privacy-mode / quota failures
    }
  }

  return true;
};

/**
 * Push `submit_register_form` only after a User row exists.
 * Idempotent per user via localStorage so retries / OAuth callback
 * reloads cannot inflate the key event.
 */
export const trackSubmitRegisterForm = (
  userId: string | null,
  method: RegisterMethod,
  email?: string | null,
): boolean => {
  if (!userId) return false;

  const local = safeLocal();
  const key = `${SUBMIT_REGISTER_FORM_FIRED_PREFIX}${userId}`;

  if (local) {
    try {
      if (local.getItem(key) === "1") return false;
    } catch {
      // fall through and still attempt to fire
    }
  }

  identifyAnalyticsUser(userId, email);
  trackProductEvent("submit_register_form", { method, user_id: userId });
  void trackTikTokCompleteRegistration(userId, email);

  if (local) {
    try {
      local.setItem(key, "1");
    } catch {
      // ignore privacy-mode / quota failures
    }
  }

  return true;
};

/**
 * Push the `register_subscribe` conversion the FIRST time a given user
 * transitions from no-plan to a trial or paid plan. Idempotent per user via `localStorage`
 * — repeat calls (page reloads, upgrades, etc.) are no-ops.
 *
 * @returns true if the event was pushed this call, false if it was a no-op.
 */
export const trackRegisterSubscribe = (ctx: SubscriptionContext): boolean => {
  const local = safeLocal();
  const key = ctx.userId ? `${REGISTER_SUBSCRIBE_FIRED_PREFIX}${ctx.userId}` : null;

  if (local && key) {
    try {
      if (local.getItem(key) === "1") return false;
    } catch {
      // fall through and still attempt to fire
    }
  }

  if (ctx.userId) identifyAnalyticsUser(ctx.userId, ctx.email);

  const { fromRegistration, ageMs } = consumeRegistrationFlag();

  push({
    event: "register_subscribe",
    ecommerce: buildEcommercePayload(ctx),
    from_registration: fromRegistration,
    ...(ageMs !== null ? { time_to_subscribe_ms: ageMs } : {}),
    ...(ctx.userId ? { user_id: ctx.userId } : {}),
  });

  void trackOpenAiPlanStarted(ctx);
  void trackTikTokPlanStarted(ctx);

  if (local && key) {
    try {
      local.setItem(key, "1");
    } catch {
      // ignore privacy-mode / quota failures
    }
  }

  return true;
};

/**
 * Product funnel events for first-resume onboarding. Never throw.
 * Always attaches `user_id` / `app_user_id` when identify has run.
 */
export const trackProductEvent = (event: string, params: Record<string, unknown> = {}): void => {
  const serialized = serializeParams(params);
  const userId =
    (typeof serialized.user_id === "string" && serialized.user_id) || cachedUserId || null;
  if (userId) {
    cachedUserId = userId;
    serialized.user_id = userId;
    serialized.app_user_id = userId;
  }
  push({ event, ...serialized });
};

/**
 * North-star conversion: first time this user has a real employer on a resume
 * during the 24h onboarding window. Idempotent per user via localStorage.
 */
export const trackFirstRealExperienceSaved = (resumeId: string): boolean => {
  if (!resumeId) return false;
  const local = safeLocal();
  const userKey = cachedUserId ? `${FIRST_REAL_EXPERIENCE_USER_PREFIX}${cachedUserId}` : null;

  if (local && userKey) {
    try {
      if (local.getItem(userKey) === "1") return false;
    } catch {
      // fall through and still attempt to fire
    }
  }

  const elapsed = onboardingElapsedMs();
  trackProductEvent("first_real_experience_saved", {
    resume_id: resumeId,
    ...(elapsed !== null ? { time_to_experience_ms: elapsed } : {}),
  });

  if (local && userKey) {
    try {
      local.setItem(userKey, "1");
    } catch {
      // ignore privacy-mode / quota failures
    }
  }

  return true;
};
